/**
 * API Functions for Notification Operations
 * Handles marking notifications as read in Drupal
 */

import type { MarkNotificationReadRequest, NotifiedUserEntry } from '../types';

/**
 * Marks a notification as read by appending user email to field_notified_users
 * Uses the Drupal API endpoint to update the notification node
 *
 * @param notificationId - The UUID of the notification to mark as read
 * @param userEmail - The email of the user who read the notification
 * @param apiBaseUrl - Optional API base URL (defaults to /api/drupal/nodes/notifications)
 * @returns Promise that resolves when the notification is marked as read
 * @throws Error if the API request fails
 */
export async function markNotificationAsRead(
  notificationId: string,
  userEmail: string,
  apiBaseUrl: string = '/api/drupal/nodes/notifications'
): Promise<void> {
  if (!notificationId) {
    throw new Error('Notification ID is required');
  }

  if (!userEmail) {
    throw new Error('User email is required');
  }

  const entry: NotifiedUserEntry = {
    email: userEmail,
    at: new Date().toISOString(),
    readable: new Date().toLocaleString(),
  };

  const requestBody: MarkNotificationReadRequest = {
    appendNotified: true,
    entry,
  };

  const response = await fetch(`${apiBaseUrl}/${encodeURIComponent(notificationId)}`, {
    method: 'PATCH',
    headers: {
      'content-type': 'application/json',
    },
    body: JSON.stringify(requestBody),
  });

  if (!response.ok) {
    throw new Error(`Failed to mark notification as read: ${response.statusText}`);
  }
}

/**
 * Marks multiple notifications as read in parallel
 * Uses Promise.allSettled to continue even if some requests fail
 *
 * @param notificationIds - Array of notification UUIDs to mark as read
 * @param userEmail - The email of the user who read the notifications
 * @param apiBaseUrl - Optional API base URL
 * @returns Promise with results indicating success/failure for each notification
 */
export async function markMultipleNotificationsAsRead(
  notificationIds: string[],
  userEmail: string,
  apiBaseUrl?: string
): Promise<PromiseSettledResult<void>[]> {
  const promises = notificationIds.map(id => markNotificationAsRead(id, userEmail, apiBaseUrl));

  return Promise.allSettled(promises);
}

/**
 * Creates a mutation function compatible with TanStack Query
 * Allows easy integration with React Query mutations
 *
 * @param userEmail - The email of the user
 * @param apiBaseUrl - Optional API base URL
 * @returns Mutation function that accepts notification ID
 */
export function createMarkAsReadMutation(
  userEmail: string,
  apiBaseUrl?: string
): (notificationId: string) => Promise<void> {
  return async (notificationId: string) => {
    return markNotificationAsRead(notificationId, userEmail, apiBaseUrl);
  };
}

